import { ColorLike, DisplaySizeLike } from 'outpost';
import { Font, HorizontalAlign, VerticalAlign } from './types.ts';
import { AssetLoader } from './asset-loader.ts';
import { isColor, isImageUrl } from './utils.ts';

const BOTTOM_CHARACTERS = new Set(['g', 'j', 'p', 'q', 'y']);
const ICON_HEIGHT_MULTIPLIER = 0.85;

export type TextToken = {
    x: number;
    y: number;
    font: string;
    size: number;
    scale: number;
    color: string;
    bold: boolean;
    italic: boolean;
    horizontalAlign: HorizontalAlign;
    verticalAlign: VerticalAlign;
    content: string;
    offset: number;
    isImage: boolean;
};
export type TextTokenRule = 'right' | 'center' | 'bold' | 'italic' | 'small' | 'big' | 'sub' | string;

export type TextParameters = {
    font: string;
    size: number;
    color: string;
    bold: boolean;
    italic: boolean;
    horizontalAlign: HorizontalAlign;
    verticalAlign: VerticalAlign;
};

export type RenderTextResult = {
    image: HTMLCanvasElement;
    dependsOnAssets: Set<string>;
};

export type TextAttributes = {
    width: number;
    height: number;
    borderRadius: DisplaySizeLike;
    horizontalAlign: HorizontalAlign;
    verticalAlign: VerticalAlign;
    text: string | null;
    textFont: Font;
    textSize: DisplaySizeLike;
    textColor: ColorLike;
    textPadding: DisplaySizeLike;
    textBold: boolean;
    textItalic: boolean;
    textMultiline: boolean;
    textCursorIndex: number;
    textCursorShow: boolean;
};

export type RenderTextParams = {
    imageLoader: AssetLoader;
    content: string;
    width: number;
    height: number;
    font: string;
    size: number;
    color: string;
    bold: boolean;
    italic: boolean;
    horizontalAlign: HorizontalAlign;
    verticalAlign: VerticalAlign;
    allowMultiline: boolean;
    padding: number;
};

export function renderText(params: RenderTextParams, textSizeMultiplier: number = 1): RenderTextResult {
    let imageLoader = params.imageLoader;
    let text = params.content;
    let width = params.width;
    let height = params.height;
    let allowMultiline = params.allowMultiline;
    let padding = params.padding;
    let textSize = params.size * textSizeMultiplier;
    let textFont = params.font;
    let textColor = params.color;
    let textBold = params.bold;
    let textItalic = params.italic;
    let textFit = true;
    let fixVerticalCenter = true;
    let maxWidth = textFit ? width : 0;
    let maxHeight = textFit ? height : 0;

    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d')!;

    textSize = Math.floor(textSize);
    padding = Math.ceil(padding);

    let baseTextParams: TextParameters = {
        font: textFont,
        size: textSize,
        color: textColor ?? 'rgba(0,0,0,0)',
        bold: textBold,
        italic: textItalic,
        horizontalAlign: 'left',
        verticalAlign: 'middle'
    };
    let lines = [];
    let maxCanvasWidth = maxWidth || Infinity;
    let maxCanvasHeight = maxHeight || Infinity;
    let maxLineWidth = maxWidth ? maxWidth - padding * 2 : Infinity;
    let maxTextHeight = maxHeight ? maxHeight - padding * 2 : Infinity;
    let startX = padding + 1;
    let startY = padding;
    let x = startX;
    let y = startY;
    let currentLine = [];
    let currentLineWidth = 0;
    let longestLineWidth = 0;
    let currentLineHeight = textSize;
    let lineHorizontalAlign = 'left';
    let previousLineHeight = 0;
    let lastToken = { content: '' };
    let tokens = tokenize(text.replaceAll('\\n', '\n') + '\n', baseTextParams);
    let dependsOnAssets: Set<string> = new Set([textFont]);

    for (let token of tokens) {
        if (token.isImage) {
            dependsOnAssets.add(token.content);
        }

        let tokenWidth = measureToken(token, ctx, imageLoader);

        let tokenHeight = token.size || 0;
        let isNewLine = token.content.includes('\n');

        if (isNewLine || (currentLineWidth + tokenWidth > maxLineWidth && currentLine.length > 0 && allowMultiline)) {
            if (currentLine.length === 0) {
                currentLineHeight = Math.ceil(textSize * 2 / 3);
            }

            let m = token.content === '@\n' ? 0.5 : 1;

            y += (currentLineHeight || previousLineHeight) * m;

            for (let obj of currentLine) {
                obj.y = y;
            }

            lines.push({
                tokens: currentLine,
                width: currentLineWidth,
                height: currentLineHeight,
                align: lineHorizontalAlign
            });

            currentLineWidth = isBlank(token) ? 0 : tokenWidth;
            x = startX;
            previousLineHeight = currentLineHeight || previousLineHeight;
            currentLineHeight = 0;
            currentLine = [];
        } else {
            currentLineWidth += tokenWidth;
        }

        if (!isNewLine) {
            currentLineHeight = Math.max(currentLineHeight, tokenHeight + 2);
            lineHorizontalAlign = token.horizontalAlign;
        }

        longestLineWidth = Math.max(longestLineWidth, currentLineWidth);

        if (!isNewLine) {
            token.x = x;

            if (token.content !== ' ' || currentLine.length > 0 || token === tokens[0]) {
                currentLine.push(token);
            }

            if (token.content !== ' ' || x !== startX || lastToken.content.includes('\n')) {
                x += tokenWidth;
            }
        }

        lastToken = token;
    }

    let additionalHeight = 0;

    for (let i = 0; i < lines.length; ++i) {
        let line = lines[i];
        let lineHeight = line.height;
        let widthDif = longestLineWidth - line.width;
        let offsetX = 0;

        if (line.align === 'center') {
            offsetX = widthDif / 2;
        } else if (line.align === 'right') {
            offsetX = widthDif;
        }

        for (let token of line.tokens) {
            let dif = (lineHeight - token.size);
            let m = 0.3;

            if (token.verticalAlign === 'bottom') {
                m = -0.1;

                if (i === lines.length - 1) {
                    additionalHeight = Math.max(additionalHeight, lineHeight * 0.2);
                }
            }

            token.x += offsetX;
            token.y -= dif * m;
        }

        if (i === lines.length - 1 && lines.length === 1) {
            additionalHeight = Math.max(additionalHeight, lineHeight * 0.1);
        }
    }

    if (fixVerticalCenter && lines.length === 1) {
        let line = lines[0];
        let has_bottom_characters = false;

        for (let token of line.tokens) {
            for (let c of token.content) {
                if (BOTTOM_CHARACTERS.has(c)) {
                    has_bottom_characters = true;
                }
            }
        }

        if (true || !has_bottom_characters) {
            for (let token of line.tokens) {
                token.y += line.height * 0.09;
            }
        }
    }

    let totalHeight = Math.round(y - startY + padding * 2 + 1 + additionalHeight);
    let totalWidth = Math.round(longestLineWidth + padding * 2 + 2);

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    // ctx.fillStyle = 'white';
    // ctx.fillRect(0, 0, canvas.width, canvas.height);

    // ctx.fillStyle = 'white'; ctx.fillRect(0, 0, totalWidth, totalHeight);

    // if (backgroundColor) {
    //     ctx.fillStyle = backgroundColor;
    //     ctx.fillRect(0, 0, totalWidth, totalHeight);
    // }

    // if (borderColor) {
    //     ctx.lineWidth = 2;
    //     ctx.strokeStyle = borderColor;
    //     ctx.strokeRect(0, 0, totalWidth, totalHeight);
    // }

    ctx.textBaseline = 'bottom';

    for (let line of lines) {
        for (let token of line.tokens) {
            let icon = getTokenIcon(token, imageLoader);
            let offsetY = Math.floor(token.offset * token.size);

            if (icon) {
                let height = token.size * icon.scale;
                let width = height * icon.image.width / icon.image.height;

                // Attempt at properly aligning the icons with the text
                offsetY -= Math.floor(((icon.scale / ICON_HEIGHT_MULTIPLIER) - 1) * token.size / 2);

                ctx.drawImage(icon.image, token.x, token.y - token.size + offsetY, width, height);
            } else {
                setCanvasPropertiesFromToken(ctx, token);
                ctx.fillText(token.content, token.x, token.y + offsetY);
            }
        }
    }

    if (canvas.width > maxCanvasWidth) {
        return renderText(params, textSizeMultiplier * maxCanvasWidth / canvas.width);
    } else if (canvas.height > maxCanvasHeight) {
        return renderText(params, textSizeMultiplier * 0.95);
    }

    return { image: canvas, dependsOnAssets };
}

function getTokenIcon(token: TextToken, assetLoader: AssetLoader): { image: HTMLImageElement, scale: number; } | null {
    if (!token.isImage) {
        return null;
    }

    let image = assetLoader.getImage(token.content);
    let scale = token.scale * ICON_HEIGHT_MULTIPLIER;

    return image && { image, scale };
}

function measureToken(token: TextToken, ctx: CanvasRenderingContext2D, assetLoader: AssetLoader): number {
    if (!token.content || !token.size) {
        return 0;
    }

    let icon = getTokenIcon(token, assetLoader);

    if (icon) {
        return icon.image.width / icon.image.height * token.size * icon.scale;
    } else {
        setCanvasPropertiesFromToken(ctx, token);
        return ctx.measureText(token.content).width;
    }
}

function isBlank(token: TextToken): boolean {
    return token.content === ' ' || token.content.includes('\n');
}

function setCanvasPropertiesFromToken(ctx: CanvasRenderingContext2D, token: TextToken) {
    let { font, size, bold, italic, color } = token;

    ctx.font = `${bold ? 'bold ' : ''}${italic ? 'italic ' : ''}${Math.round(size)}px "${font}"`;
    ctx.fillStyle = color;
}

function makeToken(content: string, textParams: TextParameters, rules: TextTokenRule[]): TextToken {
    let token: TextToken = {
        content,
        isImage: false,
        scale: 1,
        offset: 0,
        x: 0,
        y: 0,
        ...textParams,
    };

    if (isImageUrl(content)) {
        token.isImage = true;
        token.content = content;
    }

    for (let rule of rules) {
        if (rule.match(/^\d+px$/)) {
            token.size = parseInt(rule);
        } else if (rule.match(/^\d+%$/)) {
            token.scale *= parseFloat(rule) / 100;
        } else if (rule.match(/^o-?\d+%$/)) {
            token.offset = parseInt(rule.substring(1)) / 100;
        } else if (rule === 'right') {
            token.horizontalAlign = 'right';
        } else if (rule === 'center') {
            token.horizontalAlign = 'center';
        } else if (rule === 'bold') {
            token.bold = true;
        } else if (rule === 'italic') {
            token.italic = true;
        } else if (rule === 'normal') {
            token.italic = false;
            token.bold = false;
        } else if (isColor(rule)) {
            token.color = rule;
        }
    }

    return token;
}

const SHORTCUTS = {
    '*': 'bold',
    '_': 'italic',
};

function tokenize(text: string, textParams: TextParameters): TextToken[] {
    let tokens = [];
    let activeShortcuts = new Set();
    let ruleStack = [];
    let content = '';

    for (let i = 0; i <= text.length; ++i) {
        let c = text[i];

        if (c in SHORTCUTS && text[++i] !== c) {
            i -= 1;
            let rule = SHORTCUTS[c as keyof typeof SHORTCUTS];

            if (activeShortcuts.has(c)) {
                tokens.push(makeToken(content, textParams, ruleStack));
                activeShortcuts.delete(c);
                ruleStack.splice(ruleStack.lastIndexOf(rule), 1);
                content = '';
            } else {
                tokens.push(makeToken(content, textParams, ruleStack));
                activeShortcuts.add(c);
                ruleStack.push(rule);
                content = '';
            }
            // } else if (c === '@' && text[i + 1] === '\n') {
            //     tokens.push(makeToken(content, textParams, ruleStack));
            //     tokens.push(makeToken('@\n', textParams, ruleStack));
            //     ruleStack = [];
            //     activeShortcuts.clear();
            //     content = '';
            //     i += 1;
        } else if (c === '<') {
            let end1 = text.indexOf(' ', i + 1);
            let end2 = text.indexOf('>', i + 1);

            if (end1 === -1 && end2 === -1) {
                content += c;
            } else {
                let end = end1 === -1 ? end2 : (end2 === -1 ? end1 : Math.min(end1, end2));

                if (text[end] === ' ') {
                    let rules = text.substring(i + 1, end).split(',');

                    tokens.push(makeToken(content, textParams, ruleStack));
                    ruleStack.push(...rules);
                    content = '';
                    i = end;
                }
            }
        } else if (c === '>' && ruleStack.length) {
            tokens.push(makeToken(content, textParams, ruleStack));
            ruleStack.pop();
            content = '';
        } else if (c === '\n') {
            tokens.push(makeToken(content, textParams, ruleStack));
            tokens.push(makeToken('\n', textParams, ruleStack));
            ruleStack = [];
            activeShortcuts.clear();
            content = '';
        } else if (c === ' ') {
            let token = makeToken(content, textParams, ruleStack);
            let spaceToken = { ...token, content: ' ' };

            tokens.push(token, spaceToken);
            content = '';
        } else if (c === undefined) {
            tokens.push(makeToken(content, textParams, ruleStack));
        } else {
            content += c;
        }
    }

    return postProcessTokens(tokens);
}

function postProcessTokens(tokens: TextToken[]): TextToken[] {
    let regexp = /\[\w+\]/;

    if (!regexp) {
        return tokens;
    }

    let result: TextToken[] = [];

    for (let token of tokens) {
        if (!token.content) {
            continue;
        }

        let content = token.content;
        let startIndex = 0;

        while (startIndex < content.length) {
            let match = content.substring(startIndex).match(regexp);

            if (!match) {
                result.push(extractSubToken(token, startIndex));
                break;
            } else {
                let nextIndex = startIndex + match.index!;

                if (nextIndex !== startIndex) {
                    result.push(extractSubToken(token, startIndex, nextIndex));
                }

                let word = match[0];
                let endIndex = nextIndex + word.length;

                result.push(extractSubToken(token, nextIndex, endIndex));

                startIndex = endIndex;
            }
        }
    }

    return result;
}

function extractSubToken(token: TextToken, startIndex: number, endIndex?: number): TextToken {
    return {
        ...token,
        content: token.content.substring(startIndex, endIndex),
    };
}